import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@ui-kit/AlertDialog";
import { Button } from "@ui-kit/Button";
import type { ToolsError } from "../../model/types";
import { tui } from "../locale";
import { useLeadsIntoTools } from "../store/useLeadsIntoTools";
import { useReloadOffer } from "../store/useReloadOffer";
import { useWorkspaceToolsActions } from "../store/useWorkspaceToolsActions";
import { useEnabledToolsState, useWorkspaceToolsStore } from "../store/useWorkspaceToolsStore";
import { isUpdateFailure, useUnseenError, useUnseenRefusal } from "./useUnseenError";

type Shown = { failure: ToolsError; isRefusal: boolean };

/** A refused reload first: it answers what the user just asked, and no later failure may hide it. */
const useShownFailure = (): Shown | null => {
	const refusal = useUnseenRefusal();
	const error = useUnseenError();
	if (refusal) return { failure: refusal, isRefusal: true };
	return error && !isUpdateFailure(error) ? { failure: error, isRefusal: false } : null;
};

const useToolsLink = (): string | null => {
	const state = useEnabledToolsState();
	const currentCatalog = ApiUrlCreatorService.value.catalogName;
	const leadsIntoTools = useLeadsIntoTools();
	if (!state || !leadsIntoTools || currentCatalog === state.catalogName) return null;
	return state.links.catalog;
};

const explain = ({ failure, isRefusal }: Shown): string =>
	isRefusal && failure.code === "network" ? tui("reload-refused.network") : failure.message;

export const WorkspaceToolsErrorModal = () => {
	const shown = useShownFailure();
	const toolsLink = useToolsLink();
	const dismissError = useWorkspaceToolsStore((store) => store.dismissError);
	const dismissRefusal = useWorkspaceToolsStore((store) => store.dismissRefusal);
	const { reload, openLink } = useWorkspaceToolsActions();
	const reloadOffer = useReloadOffer(shown?.failure);
	if (!shown) return null;

	const close = () => (shown.isRefusal ? dismissRefusal() : dismissError(shown.failure.seq));
	// The primary action comes last; Close stays the default focus either way.
	const closeButton = (
		<AlertDialogCancel onClick={close} variant={reloadOffer === "primary" ? "outline" : "primary"}>
			{tui("error.close")}
		</AlertDialogCancel>
	);

	return (
		<AlertDialog onOpenChange={(open) => !open && close()} open>
			<AlertDialogContent status="error">
				<div className="contents" data-testid="workspace-tools-error-modal">
					<AlertDialogHeader>
						<AlertDialogTitle>
							{tui(shown.isRefusal ? "reload-refused.title" : "error.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>{explain(shown)}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="sm:flex-wrap sm:gap-y-2">
						{reloadOffer === "primary" && closeButton}
						{toolsLink && (
							<Button
								data-testid="workspace-tools-error-goto"
								onClick={() => {
									close();
									void openLink("catalog");
								}}
								variant="outline"
							>
								{tui("error.goto")}
							</Button>
						)}
						{reloadOffer && (
							<Button
								data-testid="workspace-tools-error-reload"
								onClick={() => {
									close();
									void reload();
								}}
								variant={reloadOffer === "primary" ? "primary" : "outline"}
							>
								{tui("reload")}
							</Button>
						)}
						{reloadOffer !== "primary" && closeButton}
					</AlertDialogFooter>
				</div>
			</AlertDialogContent>
		</AlertDialog>
	);
};
